import * as React from 'react';
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { getCurrentISOWeekKey, getWeekStartDateFromKey, formatWeekLabel, WeekKey } from '../utils/weekKeyUtils';
import { logger } from '../utils/logger';
import { useUser } from '../contexts/UserContext';
import { showInfoToast } from './useToast';
import { getAllWeekKeys } from '../supabase/snapshotService';

type WeekCtx = {
  selectedWeek: WeekKey;
  setWeek: (wk: WeekKey) => void;
  setToCurrent: () => void;
  label: string;
  dbWeekKeys: string[];
  viewerMaxWeek: string | null;
  refreshDbWeekKeys: () => Promise<string[]>;
};

const WeekContext = createContext<WeekCtx | null>(null);

/**
 * Week restoration telemetry (production-safe, observable via window.__marlo)
 * Records why we chose a particular week on init
 */
interface WeekRestoreTelemetry {
  storedWeekKey: string | null;
  resolvedWeekKey: string;
  fallbackReason: 'stored' | 'no_stored' | 'stored_invalid' | 'future_clamp_removed' | 'restore_error';
  errorMessage?: string;
  timestamp: string;
}

function recordWeekRestore(telemetry: WeekRestoreTelemetry): void {
  try {
    const w = typeof window !== 'undefined' ? window : null;
    if (w && typeof w === 'object') {
      const marlo = (w as Window & { __marlo?: any }).__marlo || {};
      marlo.weekRestore = telemetry;
      (w as Window & { __marlo?: any }).__marlo = marlo;
      
      // Also log in console for production debugging (user explicitly wants console logs visible)
      console.log('[WeekRestore] Week identity resolved', telemetry);
    }
  } catch {
    // Telemetry failures are non-critical
  }
}

export const WeekProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { canEdit, workspaceId } = useUser();
  const isViewer = !canEdit;
  
  // ✅ ARCHITECTURAL FIX: Manage dbWeekKeys at context level (single source of truth)
  const [dbWeekKeys, setDbWeekKeys] = useState<string[]>([]);
  
  /**
   * Fetch all week keys from database
   * Single source of truth for which weeks have data
   */
  const refreshDbWeekKeys = useCallback(async () => {
    try {
      const weekKeys = await getAllWeekKeys();
      setDbWeekKeys(weekKeys);
      
      if (import.meta.env.DEV) {
        logger.debug('[WeekProvider] Refreshed DB week keys', {
          count: weekKeys.length,
          weeks: weekKeys.sort()
        }, 'WeekProvider');
      }
      return weekKeys;
    } catch (error) {
      if (import.meta.env.DEV) {
        logger.error('[WeekProvider] Failed to load DB week keys', { error }, 'WeekProvider');
      }
      return [];
    }
  }, []);
  
  // Initial load on mount
  useEffect(() => {
    refreshDbWeekKeys();
    
    // Expose globally for useSavedSnapshots to call after save
    (window as any).__refreshWeekKeys = refreshDbWeekKeys;
    return () => {
      delete (window as any).__refreshWeekKeys;
    };
  }, [refreshDbWeekKeys]);
  
  // Refresh on auth/workspace change
  useEffect(() => {
    if (workspaceId) {
      refreshDbWeekKeys();
    }
  }, [workspaceId, canEdit, refreshDbWeekKeys]);
  
  const [selectedWeek, setSelectedWeek] = useState<WeekKey>(() => {
    const current = getCurrentISOWeekKey();
    const stored = localStorage.getItem('ui:selectedWeekKey') as WeekKey | null;
    
    // No stored key → use current (first time user or storage cleared)
    if (!stored) {
      recordWeekRestore({
        storedWeekKey: null,
        resolvedWeekKey: current,
        fallbackReason: 'no_stored',
        timestamp: new Date().toISOString()
      });
      return current;
    }
    
    try {
      // Validate stored key format (must parse to a valid date)
      const storedStart = getWeekStartDateFromKey(stored);
      if (!storedStart) {
        // Stored key is malformed or unparseable → use current
        recordWeekRestore({
          storedWeekKey: stored,
          resolvedWeekKey: current,
          fallbackReason: 'stored_invalid',
          errorMessage: 'getWeekStartDateFromKey returned null',
          timestamp: new Date().toISOString()
        });
        return current;
      }
      
      // ✅ CRITICAL FIX: Remove "future clamp" logic
      // If user was editing a past week (e.g. 12/15) and refreshes, they should return to 12/15
      // The old logic clamped "future" weeks to current, but this was too aggressive
      // and caused week identity drift when user edited past weeks
      // 
      // OLD LOGIC (removed):
      // const currentStart = getWeekStartDateFromKey(current);
      // if (storedStart.getTime() > currentStart.getTime()) {
      //   return current; // Clamp to current if stored is "future"
      // }
      //
      // NEW LOGIC: Trust stored key unconditionally (it's what user last edited)
      // The only restriction is: stored key must be valid (parseable, not malformed)
      
      recordWeekRestore({
        storedWeekKey: stored,
        resolvedWeekKey: stored,
        fallbackReason: 'stored',
        timestamp: new Date().toISOString()
      });
      
      return stored;
      
    } catch (error) {
      // Restore failed (e.g. throw from getWeekStartDateFromKey) → use current
      // This should be rare after recursion fixes, but we still guard it
      const errorMessage = error instanceof Error ? error.message : String(error);
      recordWeekRestore({
        storedWeekKey: stored,
        resolvedWeekKey: current,
        fallbackReason: 'restore_error',
        errorMessage,
        timestamp: new Date().toISOString()
      });
      
      logger.error('Week restore failed, falling back to current week', { 
        stored, 
        current, 
        error 
      }, 'useWeekSelection');
      
      return current;
    }
  });

  useEffect(() => {
    try { 
      localStorage.setItem('ui:selectedWeekKey', selectedWeek);
      
      // Record week change in telemetry (production-safe)
      try {
        const w = typeof window !== 'undefined' ? window : null;
        if (w && typeof w === 'object') {
          const marlo = (w as Window & { __marlo?: any }).__marlo || {};
          marlo.currentWeekKey = selectedWeek;
          marlo.lastWeekChangeTimestamp = new Date().toISOString();
          (w as Window & { __marlo?: any }).__marlo = marlo;
        }
      } catch {
        // Telemetry failures are non-critical
      }
    } catch (error) {
      // localStorage failures are non-critical, but log for production debugging
      logger.error('localStorage write failed (CRITICAL: week identity may not persist)', { 
        error, 
        selectedWeek 
      }, 'useWeekSelection');
      
      // Also record in telemetry so we can detect when persistence fails
      try {
        const w = typeof window !== 'undefined' ? window : null;
        if (w && typeof w === 'object') {
          const marlo = (w as Window & { __marlo?: any }).__marlo || {};
          marlo.weekPersistenceError = {
            week: selectedWeek,
            error: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          };
          (w as Window & { __marlo?: any }).__marlo = marlo;
        }
      } catch {
        // Telemetry failures are non-critical
      }
    }
  }, [selectedWeek]);
  
  /**
   * ✅ VIEWER MAX WEEK CALCULATION
   * 
   * Latest week that has data (viewers can be on current week if it has data).
   * Used as redirect target when viewer lands on a no-data week (e.g. via URL).
   */
  const viewerMaxWeek = useMemo(() => {
    if (!isViewer) return null;
    if (dbWeekKeys.length === 0) return getCurrentISOWeekKey();
    const sorted = [...dbWeekKeys].sort();
    return sorted[sorted.length - 1];
  }, [isViewer, dbWeekKeys]);
  
  /**
   * ✅ VIEWER NAVIGATION GUARD
   * 
   * Viewers may only be on weeks that have data (no forward-fill from clicking no-data weeks).
   * If they land on a no-data week (URL/history), redirect to latest week with data.
   */
  useEffect(() => {
    if (!isViewer || !viewerMaxWeek) return;
    
    const weekHasData = dbWeekKeys.includes(selectedWeek);
    if (!weekHasData) {
      logger.warn('[ViewerGuard] Viewer on no-data week, redirecting', {
        attemptedWeek: selectedWeek,
        redirectTo: viewerMaxWeek
      }, 'WeekProvider');
      setSelectedWeek(viewerMaxWeek);
      showInfoToast('Showing latest available data', 'Viewer access is limited to weeks with saved data.');
    }
  }, [selectedWeek, isViewer, viewerMaxWeek, dbWeekKeys]);

  const value = useMemo<WeekCtx>(() => ({
    selectedWeek,
    setWeek: setSelectedWeek,
    setToCurrent: () => setSelectedWeek(getCurrentISOWeekKey()),
    label: formatWeekLabel(selectedWeek),
    dbWeekKeys,
    viewerMaxWeek,
    refreshDbWeekKeys
  }), [selectedWeek, dbWeekKeys, viewerMaxWeek, refreshDbWeekKeys]);

  return React.createElement(WeekContext.Provider, { value }, children);
};

export function useWeekCalculation(): WeekCtx {
  const ctx = useContext(WeekContext);
  if (!ctx) throw new Error('useWeekCalculation must be used within <WeekProvider>');
  return ctx;
}
